// Button variants + types live in a .ts (not the .svelte module script) so plain
// `tsc` consumers can import the named `buttonVariants` value and the variant
// types — the default-only `*.svelte` ambient can't expose named exports.
import { type VariantProps, tv } from "tailwind-variants";
import type { HTMLAnchorAttributes, HTMLButtonAttributes } from "svelte/elements";
import type { WithElementRef } from "../../../utils.js";

export const buttonVariants = tv({
  base: "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent text-sm font-semibold outline-none transition-colors duration-100 select-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-surface disabled:pointer-events-none disabled:opacity-55 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  variants: {
    variant: {
      primary: "bg-brand text-on-brand hover:bg-brand-strong",
      secondary: "border-border bg-surface text-brand-strong hover:border-brand",
      outline: "border-border bg-surface text-ink hover:bg-surface-muted",
      ghost: "text-muted hover:bg-surface-muted hover:text-ink",
      destructive: "bg-danger text-on-brand hover:opacity-90",
      "destructive-ghost": "text-muted hover:bg-danger-bg hover:text-danger",
      link: "border-none text-brand-strong underline-offset-4 hover:underline",
    },
    size: {
      default: "h-9 px-4",
      xs: "h-6 gap-1 px-2 text-xs",
      sm: "h-8 px-3",
      lg: "h-11 px-6 text-lg",
      icon: "size-9",
      "icon-sm": "size-8",
      "icon-xs": "size-6",
      "icon-lg": "size-10",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "default",
  },
});

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];
export type ButtonSize = VariantProps<typeof buttonVariants>["size"];

export type ButtonProps = WithElementRef<HTMLButtonAttributes> &
  WithElementRef<HTMLAnchorAttributes> & {
    variant?: ButtonVariant;
    size?: ButtonSize;
  };
